<template>
  <v-dialog
    v-model="dialog"
    max-width="400"
    @click:outside="closeModal"
  >
    <v-card class="contact-dealer-modal" :class="{ 'dark-mode': isDarkMode }">
      <!-- Header -->
      <v-card-title class="modal-header">
        <div class="header-content">
          <div class="vehicle-info">
            <h3 class="vehicle-title">{{ getVehicleTitle() }}</h3>
          </div>
          <v-btn
            icon
            small
            @click="closeModal"
            class="close-btn"
            color="black"
          >
            <v-icon class="material-icons-round" color="black">close</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <!-- Contact Options -->
      <v-card-text class="contact-container">
        <div class="contact-prompt">
          <span class="prompt-text">Prefer to reach them now?</span>
        </div>

        <!-- Call Dealer Option -->
        <a :href="'tel:' + dealerPhone" class="contact-option call-option">
          <div class="option-icon">
            <i class="material-icons-round">phone</i>
          </div>
          <div class="option-content">
            <div class="option-title">Call Dealer</div>
            <div class="option-value">{{ formatPhoneNumber(dealerPhone) }}</div>
            <div class="option-hint">Tap to call directly</div>
          </div>
          <div class="option-arrow">
            <i class="material-icons-round">chevron_right</i>
          </div>
        </a>

        <!-- Email Dealer Option -->
        <a :href="'mailto:' + dealerEmail + '?subject=Inquiry about ' + getVehicleTitle()" class="contact-option email-option">
          <div class="option-icon email">
            <i class="material-icons-round">email</i>
          </div>
          <div class="option-content">
            <div class="option-title">Email Dealer</div>
            <div class="option-value">{{ dealerEmail }}</div>
          </div>
          <div class="option-arrow">
            <i class="material-icons-round">chevron_right</i>
          </div>
        </a>
      </v-card-text>

      <!-- Footer -->
      <v-card-actions class="modal-footer">
        <v-btn
          text
          block
          class="close-modal-btn"
          @click="closeModal"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'ContactDealerModal',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    vehicle: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      dialog: false,
      isDarkMode: false,
      company: null
    }
  },
  computed: {
    dealerPhone() {
      return this.company?.phone_m || this.company?.phone || '(602) 610-1072'
    },
    dealerEmail() {
      return this.company?.email || 'contact@autosyncx.com'
    }
  },
  watch: {
    value(newVal) {
      this.dialog = newVal
    },
    dialog(newVal) {
      this.$emit('input', newVal)
    }
  },
  mounted() {
    this.loadDarkMode()
    this.loadCompany()
  },
  methods: {
    loadDarkMode() {
      const savedDarkMode = localStorage.getItem('isDarkMode')
      this.isDarkMode = savedDarkMode === 'true'
    },

    loadCompany() {
      try {
        const companyData = localStorage.getItem('company')
        if (companyData) {
          this.company = JSON.parse(companyData)
        }
      } catch (e) {
        console.error('Error loading company:', e)
      }
    },

    getVehicleTitle() {
      if (!this.vehicle) return 'Vehicle'

      const parts = []
      if (this.vehicle.year) parts.push(this.vehicle.year)
      if (this.vehicle.make) parts.push(this.vehicle.make)
      if (this.vehicle.model) parts.push(this.vehicle.model)
      if (this.vehicle.trim) parts.push(this.vehicle.trim)

      return parts.join(' ') || 'Vehicle'
    },

    formatPrice(price) {
      if (!price) return '$0'

      const numPrice = parseFloat(price)
      if (isNaN(numPrice) || numPrice <= 0) return '$0'

      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(numPrice)
    },

    formatPhoneNumber(phone) {
      if (!phone) return '(XXX) XXX-XXXX'

      // Remove all non-digits
      const cleaned = phone.replace(/\D/g, '')

      // Format as (XXX) XXX-XXXX
      if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
      } else if (cleaned.length === 11 && cleaned[0] === '1') {
        return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
      }

      return phone
    },

    closeModal() {
      this.dialog = false
    }
  }
}
</script>

<style scoped>
/* Modal Styles */
.contact-dealer-modal {
  border-radius: 16px !important;
  overflow: hidden;
  background: #ffffff;
}

.modal-header {
  background: linear-gradient(135deg, #7c5cff 0%, #6366f1 100%);
  padding: 20px !important;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
}

.vehicle-info {
  flex: 1;
}

.vehicle-title {
  font-size: 1.125rem !important;
  font-weight: 600 !important;
  color: #ffffff !important;
  margin: 0 !important;
  line-height: 1.3;
}

.vehicle-price {
  font-size: 1.25rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  margin: 8px 0 0 0;
}

.close-btn {
  color: rgba(255, 255, 255, 0.8) !important;
}

.close-btn:hover {
  color: #ffffff !important;
}

/* Contact Container */
.contact-container {
  padding: 24px 20px !important;
}

.contact-prompt {
  text-align: center;
  margin-bottom: 20px;
}

.prompt-text {
  font-size: 1rem;
  font-weight: 500;
  color: #6b7280;
}

/* Contact Options */
.contact-option {
  display: flex;
  align-items: center;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 12px;
  margin-bottom: 12px;
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid #e9ecef;
}

.contact-option:hover {
  background: #f0f4ff;
  border-color: #7c5cff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(124, 92, 255, 0.15);
}

.contact-option:last-of-type {
  margin-bottom: 0;
}

.option-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  flex-shrink: 0;
}

.option-icon.email {
  background: linear-gradient(135deg, #7c5cff 0%, #6366f1 100%);
}

.option-icon i {
  color: #ffffff;
  font-size: 24px;
}

.option-content {
  flex: 1;
}

.option-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 2px;
}

.option-value {
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
}

.option-hint {
  font-size: 0.75rem;
  color: #9ca3af;
  margin-top: 2px;
}

.option-arrow {
  color: #9ca3af;
  margin-left: 8px;
}

.option-arrow i {
  font-size: 20px;
}

/* Modal Footer */
.modal-footer {
  padding: 12px 20px 20px !important;
  border-top: 1px solid #e9ecef;
}

.close-modal-btn {
  text-transform: none !important;
  font-weight: 500 !important;
  color: #6b7280 !important;
}

/* Dark Mode */
.contact-dealer-modal.dark-mode {
  background: #1e1535 !important;
}

.contact-dealer-modal.dark-mode .modal-header {
  background: linear-gradient(135deg, #7c5cff 0%, #8b5cf6 100%);
}

.contact-dealer-modal.dark-mode .contact-container {
  background: #1e1535;
}

.contact-dealer-modal.dark-mode .prompt-text {
  color: #a0aec0;
}

.contact-dealer-modal.dark-mode .contact-option {
  background: #241b3d;
  border-color: #2d2d44;
}

.contact-dealer-modal.dark-mode .contact-option:hover {
  background: #2d2548;
  border-color: #7c5cff;
}

.contact-dealer-modal.dark-mode .option-title {
  color: #a0aec0;
}

.contact-dealer-modal.dark-mode .option-value {
  color: #ffffff;
}

.contact-dealer-modal.dark-mode .option-hint {
  color: #6b7280;
}

.contact-dealer-modal.dark-mode .option-arrow {
  color: #6b7280;
}

.contact-dealer-modal.dark-mode .modal-footer {
  border-top-color: #2d2d44;
}

.contact-dealer-modal.dark-mode .close-modal-btn {
  color: #a0aec0 !important;
}

/* Responsive */
@media (max-width: 600px) {
  .contact-dealer-modal {
    border-radius: 16px !important;
  }

  .vehicle-title {
    font-size: 1rem !important;
  }

  .vehicle-price {
    font-size: 1.125rem;
  }
}
</style>
